// hooks/usePayments.ts
import { useQuery } from '@tanstack/react-query';
import { SupabaseQueryBuilder } from '../lib/supabaseQueryBuilder.ts';
import { QueryOptions, PaginatedResponse } from '../types/query.types.ts';
import { AccountingStatus, Payment } from '../types';
import { supabase } from '../lib/supabaseClient';
import {
    normalizeMissionAssignmentAccountingStatus,
    normalizeMissionStatusToAccounting,
} from '@/utils/missionAccounting';
import {
    MATCH_ACCOUNTING_LEAGUE_COLUMNS,
    mapMatchAssignmentToPayment,
    withAccountingLeagueScope,
} from '@/lib/payments/matchAssignmentPayment';
import type { LeagueLevelRef } from '@/lib/leagueScope/matchScope';

const resolveMissionPaymentAccountingStatus = (
    assignment: { accounting_status?: string | null; status?: string | null },
    mission?: { mission_status?: string | null }
): AccountingStatus => {
    const accountingStatus = normalizeMissionAssignmentAccountingStatus(
        assignment.accounting_status
    );
    if (accountingStatus !== AccountingStatus.NOT_ENTERED) {
        return accountingStatus;
    }

    const workflowStatus = normalizeMissionAssignmentAccountingStatus(
        assignment.status
    );
    if (workflowStatus !== AccountingStatus.NOT_ENTERED) {
        return workflowStatus;
    }

    return normalizeMissionStatusToAccounting(mission?.mission_status);
};

/** Root of the usePayments key; prefix-invalidated by realtime (lib/realtime/globalRealtimeHandlers.ts). */
export const PAYMENTS_QUERY_KEY_PREFIX = 'payments';

export function usePayments(options: QueryOptions & {
    slim?: boolean;
    includeMissions?: boolean;
    matchAccountingStatus?: AccountingStatus | AccountingStatus[];
    excludePaid?: boolean;
    fetchAll?: boolean;
    season?: string | null;
    /**
     * All leagues (useLeagues): with `filters.leagueId`, a regional / wilaya
     * league also gets the external matches it organises (accounting league
     * rule, lib/leagueScope/matchScope.ts). Without it, its own groups only.
     */
    accountingLeagues?: readonly (LeagueLevelRef & { code?: string | null; name?: string | null })[] | null;
} = {}) {
    // fetchAll: page through ALL match_assignments instead of a single capped page.
    // Required by accounting flows (corrections/EDI) where truncating to one page
    // silently drops payments once a league exceeds the page size.
    const { slim, includeMissions = true, matchAccountingStatus, excludePaid, fetchAll, season, accountingLeagues, ...rest } = options as any;

    // Extract date filters from filters object (used for server-side month filtering)
    const dateFrom = (rest.filters as any)?.dateFrom;
    const dateTo = (rest.filters as any)?.dateTo;
    const hasDateFilter = !!(dateFrom || dateTo);

    // Remap dateFrom/dateTo → matchDateFrom/matchDateTo for the SupabaseQueryBuilder,
    // and remove the original keys so they don't get processed by the generic filter logic.
    if (rest.filters) {
        const filters = { ...rest.filters };
        if (dateFrom) {
            filters.matchDateFrom = dateFrom;
            delete filters.dateFrom;
        }
        if (dateTo) {
            filters.matchDateTo = dateTo;
            delete filters.dateTo;
        }
        rest.filters = filters;
    }

    // Inject matchAccountingStatus, excludePaid and season into rest.filters for the query builder
    if (matchAccountingStatus || excludePaid || season) {
        const filters = rest.filters ? { ...rest.filters } : {};
        if (matchAccountingStatus) filters.matchAccountingStatus = matchAccountingStatus;
        if (excludePaid) filters.isPaid = false;
        if (season) filters.matchSeason = season;
        rest.filters = filters;
    }

    // When date / accounting status / season filters are present, use !inner join
    // on matches so PostgREST excludes rows where the match doesn't satisfy the filter.
    // Without !inner, an outer join would keep the row but null the match data.
    // League filter (filters.leagueId → matches.league_groups.league_id): both the
    // match and league_groups embeds must be !inner for the server to drop other
    // leagues' rows (an outer embed is only nulled; the client-side filter below
    // stays as a safety net).
    const rawLeagueFilter = (rest.filters as any)?.leagueId;
    const hasLeagueFilter = Array.isArray(rawLeagueFilter)
        ? rawLeagueFilter.filter(Boolean).length > 0
        : Boolean(rawLeagueFilter);
    const needsInnerJoin = hasDateFilter || !!matchAccountingStatus || !!season || hasLeagueFilter;
    const matchJoin = needsInnerJoin ? 'match:matches!inner(' : 'match:matches(';
    const leagueGroupJoin = hasLeagueFilter
        ? 'leagueGroup:league_groups!matches_league_group_id_fkey!inner('
        : 'leagueGroup:league_groups!matches_league_group_id_fkey(';

    // When slim is true we fetch only essential columns to reduce payload.
    const slimSelect = `
            id,
            role,
            official_id,
            original_official_id,
            indemnity_amount,
            travel_distance_km,
            notes,
            match_id,
            is_paid,
            paid_at,
            payment_batch_id,
            is_not_assigned,
            is_absent,
            payment_month_override,
            base_indemnity,
            irg_amount,
            net_amount,
            distance_bonus,
            ${matchJoin}
                id,
                match_date,
                game_day,
                cup_round,
                league_group_id,
                accounting_status,
                rejection_reason,
                rejection_comment,
                validated_by,
                validated_at,${MATCH_ACCOUNTING_LEAGUE_COLUMNS},
                ${leagueGroupJoin}
                    id,
                    league_id,
                    kind,
                    league:leagues!league_groups_league_id_fkey(id,name)
                ),
                homeTeam:teams!matches_home_team_id_fkey(id,name),
                awayTeam:teams!matches_away_team_id_fkey(id,name),
                validatedBy:profiles!matches_validated_by_fkey(full_name)
            ),
            official:officials!match_assignments_official_id_fkey(id,first_name,last_name,first_name_ar,last_name_ar,full_name,bank_account_number,category),
            paymentBatchInfo:accounting_periods!match_assignments_payment_batch_id_fkey(payment_batches!payment_batches_id_fkey(reference))
        `;

    // fullSelect now uses explicit columns (same as slimSelect) to reduce payload
    const fullSelect = slimSelect;

    // Match-assignment query with the accounting league scope; the mission query
    // keeps reading rest.filters.leagueId (only the selected league).
    const matchQueryOptions = rest.filters
        ? { ...rest, filters: withAccountingLeagueScope(rest.filters, accountingLeagues) }
        : rest;
    const accountingScope = matchQueryOptions.filters?.matchExternalOrganiserIds ?? null;

    return useQuery<PaginatedResponse<Payment>>({
        queryKey: [PAYMENTS_QUERY_KEY_PREFIX, { ...rest, accountingScope, slim: !!slim, includeMissions, dateFrom, dateTo, matchAccountingStatus, excludePaid, fetchAll: !!fetchAll, season: season ?? null }],
        queryFn: async () => {
            // Build mission query upfront so both can run in parallel
            const missionJoin = (hasDateFilter || season)
                ? 'mission:missions!inner!mission_assignments_mission_id_fkey('
                : 'mission:missions!mission_assignments_mission_id_fkey(';

            const missionSelect = `
                id,
                mission_id,
                official_id,
                role_label,
                status,
                payment_amount,
                travel_distance_km,
                accounting_status,
                is_absent,
                notes,
                validated_by,
                validated_at,
                rejection_reason,
                rejection_comment,
                league_id,
                is_paid,
                paid_at,
                payment_batch_id,
                payment_month_override,
                irg_amount,
                net_amount,
                distance_bonus,
                official:officials!mission_assignments_official_id_fkey(
                    id, first_name, last_name, first_name_ar, last_name_ar, full_name, bank_account_number, category
                ),
                ${missionJoin}
                    id, title, code, target_type, target_name, start_at, league_id, mission_status,
                    kind:mission_kinds(code)
                ),
                paymentBatchInfo:accounting_periods!mission_assignments_payment_batch_id_fkey(payment_batches!payment_batches_id_fkey(reference))
            `;

            const leagueIdFilter = (rest.filters as any)?.leagueId;

            const buildMissionQuery = () => {
                let q = supabase
                    .from('mission_assignments')
                    .select(missionSelect)
                    .not('official_id', 'is', null)
                    // Une affectation annulée (audit de stade annulé puis recréé, officiel
                    // retiré) n'est pas une indemnité à saisir : elle restait pourtant sur le
                    // kanban « À saisir ». On garde celles déjà payées, pour l'historique.
                    .or('status.neq.cancelled,is_paid.eq.true');

                if (dateFrom) q = q.gte('missions.start_at', dateFrom);
                if (dateTo) q = q.lte('missions.start_at', dateTo);
                if (season) q = q.eq('missions.season', season);

                if (leagueIdFilter) {
                    if (Array.isArray(leagueIdFilter)) {
                        q = q.in('league_id', leagueIdFilter);
                    } else {
                        q = q.eq('league_id', leagueIdFilter);
                    }
                }

                if (excludePaid) q = q.eq('is_paid', false);

                return q;
            };

            // Page through every match_assignments row (PostgREST/Supabase caps a
            // single response, so a one-shot large pageSize silently truncates).
            const fetchAllMatchAssignments = async () => {
                const PAGE = 1000;
                let page = 1;
                let acc: any[] = [];
                let lastResp: any = { data: [] };
                // Safety bound: 100 pages × 1000 = 100k rows.
                for (let i = 0; i < 100; i++) {
                    lastResp = await SupabaseQueryBuilder.executePaginatedQuery<any>(
                        'match_assignments',
                        slim ? slimSelect : fullSelect,
                        // Stable sort is REQUIRED: paginating with .range() and no
                        // ORDER BY can skip/duplicate rows across pages.
                        { ...matchQueryOptions, pagination: { page, pageSize: PAGE, sortBy: 'id', sortOrder: 'asc' } }
                    );
                    const chunk = lastResp.data || [];
                    acc = acc.concat(chunk);
                    if (chunk.length < PAGE) break;
                    page += 1;
                }
                return { ...lastResp, data: acc, count: acc.length };
            };

            // Run both queries in parallel
            const [assignmentsResp, missionResult] = await Promise.all([
                fetchAll
                    ? fetchAllMatchAssignments()
                    : SupabaseQueryBuilder.executePaginatedQuery<any>(
                        'match_assignments',
                        slim ? slimSelect : fullSelect,
                        matchQueryOptions
                    ),
                includeMissions ? buildMissionQuery() : Promise.resolve(null),
            ]);

            // Map match assignments to Payment shape
            const matchPayments: Payment[] = (assignmentsResp.data || [])
                .map(mapMatchAssignmentToPayment)
                .filter((p: Payment | null): p is Payment => p !== null);

            let allPayments = matchPayments;

            // Process mission results (already fetched in parallel)
            if (includeMissions && missionResult) {
                const { data: missionData, error } = missionResult;

                if (error) {
                    console.error("usePayments: mission query failed", {
                        message: error.message,
                        details: (error as any)?.details,
                        hint: (error as any)?.hint,
                        code: error.code,
                        leagueIdFilter: leagueIdFilter || null,
                    });
                }

                if (!error && missionData) {
                    let missionPayments: Payment[] = missionData
                        .filter((ma: any) => !ma.is_absent)
                        .map((ma: any) => {
                            const official = ma.official;
                            const mission = ma.mission;
                            const travelKm = Number(ma.travel_distance_km ?? 0);
                            const distanceBonusValue = ma.distance_bonus !== null ? Number(ma.distance_bonus) : (travelKm >= 1000 ? 10000 : 0);
                            const indemnity = Number(ma.payment_amount ?? 0) + distanceBonusValue;

                            const rawMissionIrg = ma.irg_amount !== null ? Number(ma.irg_amount) : 0;
                            const missionStatus = (ma.accounting_status as AccountingStatus) || AccountingStatus.NOT_ENTERED;
                            const isMissionFinalized = missionStatus === AccountingStatus.VALIDATED || missionStatus === AccountingStatus.CLOSED;

                            const irgAmount = (isMissionFinalized || rawMissionIrg !== 0) ? (ma.irg_amount !== null ? Number(ma.irg_amount) : null) : null;

                            const rawTotal = ma.net_amount !== null ? Number(ma.net_amount) : indemnity;
                            const total = isMissionFinalized ? rawTotal : 0;

                            const accountingStatus = resolveMissionPaymentAccountingStatus(
                                ma,
                                mission
                            );

                            const leagueId =
                                ma.league_id || mission?.league_id || '';

                            return {
                                id: ma.id,
                                officialId: ma.official_id,
                                matchId: undefined,
                                missionId: ma.mission_id,
                                assignmentType: 'mission',
                                missionKindCode: mission?.kind?.code ?? null,
                                leagueId,
                                groupId: undefined,
                                matchDescription: mission?.title || `Mission ${mission?.code || ''}`,
                                matchDate: mission?.start_at ? new Date(mission.start_at).toISOString().split('T')[0] : '',
                                officialName: official?.full_name || [official?.first_name, official?.last_name].filter(Boolean).join(' ') || '',
                                officialNameAr: [official?.first_name_ar, official?.last_name_ar].filter(Boolean).join(' ') || null,
                                officialCategory: official?.category ?? null,
                                bankAccountNumber: official?.bank_account_number || null,
                                role: ma.role_label || 'Auditeur',
                                indemnity,
                                baseIndemnity: Number(ma.payment_amount ?? 0),
                                travelDistanceInKm: travelKm,
                                distanceBonus: distanceBonusValue,
                                irgAmount,
                                total,
                                accountingStatus,
                                notes: ma.notes || null,
                                originalOfficialId: null,
                                validatedByUserId: ma.validated_by || null,
                                validatedAt: ma.validated_at || null,
                                validatedByName: undefined,
                                rejectionReason: ma.rejection_reason ?? null,
                                rejectionComment: ma.rejection_comment ?? null,
                                isPaid: ma.is_paid || false,
                                paidAt: ma.paid_at || null,
                                paymentBatchId: ma.payment_batch_id || null,
                                paymentBatchReference: ma.paymentBatchInfo?.payment_batches?.reference || null,
                                paymentMonthOverride: ma.payment_month_override || null,
                            } as Payment;
                        });

                    if (leagueIdFilter) {
                        const matchLeague = (leagueId: string) => {
                            if (!leagueId) return false;
                            if (Array.isArray(leagueIdFilter)) {
                                return leagueIdFilter.includes(leagueId);
                            }
                            return leagueId === leagueIdFilter;
                        };

                        missionPayments = missionPayments.filter((payment) =>
                            matchLeague(payment.leagueId)
                        );
                    }

                    allPayments = [...matchPayments, ...missionPayments];
                }
            }

            return {
                ...assignmentsResp,
                data: allPayments,
                count: allPayments.length,
            } as PaginatedResponse<Payment>;
        },
        enabled: (rest as any).enabled !== false,
    });
}